import React, { useEffect, useState } from 'react';

// Formato de fecha largo
const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-PE', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

// Formato de hora
const fmtTime = (timeStr) => {
  if (!timeStr) return '';
  return timeStr;
};

// Divisor elegante personalizado
const ElegantDivider = ({ color = 'rgba(255,255,255,0.5)' }) => (
  <div className="flex items-center justify-center gap-4 my-6 w-full max-w-[240px] mx-auto">
    <div className="h-px flex-1 bg-gradient-to-r from-transparent to-current opacity-60" style={{ color }} />
    <div className="w-1.5 h-1.5 rotate-45 border border-current opacity-80" style={{ color }} />
    <div className="h-px flex-1 bg-gradient-to-l from-transparent to-current opacity-60" style={{ color }} />
  </div>
);

export default function CoverIntro({ event, cover, onEnter, accent = '#ffffff', fontFamily }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Pequeño delay para activar la animación de entrada suave
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const d = event.data || {};
  const names = (() => {
    if (event.template?.slug?.includes('boda')) {
      return `${d.bride || 'María'} & ${d.groom || 'Carlos'}`;
    }
    if (event.template?.category === 'Cumpleaños') return d.name || 'Festejado';
    if (event.template?.category === 'Baby Shower') return d.babyName || 'Bebé';
    if (event.template?.category === 'Bautizos') return d.child || 'Niño/a';
    if (event.template?.category === 'Graduaciones') return d.graduate || 'Graduado';
    return event.title;
  })();

  const dateStr = fmtDate(d.date);
  const timeStr = fmtTime(d.time);
  const place = d.church || d.place || d.civilPlace;
  const coverUrl = cover?.url;

  // Determinar colores basados en si hay imagen o no
  const textColor = coverUrl ? 'rgba(255,255,255,0.9)' : accent;
  const mutedColor = coverUrl ? 'rgba(255,255,255,0.7)' : (accent === '#ffffff' ? '#94a3b8' : accent);
  const dividerColor = coverUrl ? 'rgba(255,255,255,0.6)' : accent;

  return (
    <div className="fixed inset-0 z-50 w-full h-screen overflow-hidden bg-slate-950">
      {/* Fondo: cover photo o gradiente premium */}
      {coverUrl ? (
        <>
          <img
            src={coverUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover scale-105 animate-[slowZoom_20s_ease-in-out_infinite_alternate]"
            style={{ objectPosition: 'center 30%' }}
          />
          {/* Overlay oscuro sofisticado */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        </>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
      )}

      {/* Marco decorativo sutil (doble línea) */}
      <div className="absolute inset-3 sm:inset-5 border border-white/10 pointer-events-none rounded-sm" />
      <div className="absolute inset-4 sm:inset-6 border border-white/5 pointer-events-none rounded-sm" />

      {/* Contenido principal con animación de entrada */}
      <div 
        className={`relative z-10 w-full h-full flex flex-col transition-all duration-1000 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >


        {/* Centro - contenido principal */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          
          {/* Etiqueta superior */}
          <p
            className="text-[10px] sm:text-xs tracking-[0.5em] uppercase mb-2"
            style={{ color: textColor, fontFamily: fontFamily || "'Playfair Display', serif", fontWeight: 500 }}
          >
            Tenemos el placer de invitarte a
          </p>

          {/* Divisor elegante */}
          <ElegantDivider color={dividerColor} />

          {/* Nombres con tipografía refinada */}
          <h1
            className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-light text-white leading-[1.1] mb-2"
            style={{ 
              fontFamily: fontFamily || "'Playfair Display', serif",
              textShadow: coverUrl ? '0 4px 30px rgba(0,0,0,0.5)' : 'none'
            }}
          >
            {names.split('&').map((part, i, arr) => (
              <React.Fragment key={i}>
                <span className="italic font-normal">{part.trim()}</span>
                {i < arr.length - 1 && (
                  <span
                    className="block sm:inline text-3xl sm:text-5xl font-light my-2 sm:my-0 sm:mx-4 opacity-80"
                    style={{ fontFamily: "'Cormorant Garamond', serif", color: dividerColor }}
                  >
                    &amp;
                  </span>
                )}
              </React.Fragment>
            ))}
          </h1>

          {/* Detalles en línea elegante */}
          {(dateStr || timeStr || place) && (
            <div className="mt-8 flex flex-col items-center gap-3">
              {dateStr && (
                <p className="text-sm sm:text-base font-light tracking-[0.15em] text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.125rem' }}>
                  {dateStr}
                </p>
              )}
              
              <div className="flex items-center gap-4 text-white/70 text-xs tracking-wider">
                {timeStr && (
                  <span className="flex items-center gap-1.5">
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5l3 2" />
                    </svg>
                    {timeStr}
                  </span>
                )}
                {timeStr && place && <span className="w-1 h-1 rounded-full bg-white/40" />}
                {place && (
                  <span className="flex items-center gap-1.5">
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    {place}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Botones de acción premium */}
          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md mt-12">
            <button
              onClick={onEnter}
              className="group relative flex-1 px-8 py-4 overflow-hidden rounded-full border border-white/30 bg-white/5 backdrop-blur-md text-white transition-all duration-300 hover:bg-white/10 hover:border-white/50 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]"
            >
              <span className="relative z-10 flex items-center justify-center gap-2 text-[11px] font-semibold tracking-[0.25em] uppercase">
                Confirmar asistencia
                <svg viewBox="0 0 24 24" className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </span>
            </button>
{/*             
            <button
              onClick={onEnter}
              className="flex-1 px-8 py-4 rounded-full border border-transparent text-white/80 text-[11px] font-semibold tracking-[0.25em] uppercase transition-all duration-300 hover:text-white hover:bg-white/5"
            >
              Ver detalles
            </button> */}
            
          </div>
        </div>

        {/* Indicador scroll elegante */}
        <div className="pb-8 sm:pb-10 flex justify-center shrink-0">
          <button
            onClick={onEnter}
            className="group flex flex-col items-center gap-2 text-white/50 hover:text-white/80 transition-colors duration-300"
            aria-label="Abrir invitación"
          >
            <span className="text-[9px] tracking-[0.3em] uppercase">Descubrir</span>
            <div className="w-6 h-10 rounded-full border border-white/30 flex justify-center pt-2 group-hover:border-white/60 transition-colors">
              <div className="w-1 h-1 rounded-full bg-white animate-[bounce_2s_infinite]" />
            </div>
          </button>
        </div>
      </div>

      {/* Estilos de animación personalizados inline */}
      <style>{`
        @keyframes slowZoom {
          0% { transform: scale(1.05); }
          100% { transform: scale(1.15); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(4px); }
        }
      `}</style>
    </div>
  );
}